"use client";

import { useEffect, useRef, useState } from "react";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type AdSlot =
  | "header"
  | "index_top"
  | "index_bottom"
  | "post_top"
  | "post_bottom"
  | "sidebar_1"
  | "sidebar_2"
  | "in_article_1"
  | "in_article_2"
  | "posts_top"
  | "posts_bottom"
  | (string & {});

interface AdItem {
  id: string;
  lang_id: string;
  ad_space: string;
  ad_code_desktop: string | null;
  desktop_width: string;
  desktop_height: string;
  ad_code_mobile: string | null;
  mobile_width: string;
  mobile_height: string;
  display_category_id: string | null;
  paragraph_number: string | null;
}

interface AdApiResponse {
  status: string;
  data: AdItem[][];
}

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

export interface CollaborationMarqueeProps {
  heading?: string;
  speed?: number; // pixels per frame
  gap?: number; // px between items
  itemHeight?: number; // height of each item
  lang?: number;
  className?: string;
  only?: string;
}

// ─────────────────────────────────────────────────────────────
// Shared cache
// ─────────────────────────────────────────────────────────────

const _cache = new Map<string, { items: AdItem[]; at: number }>();
const _TTL = 5 * 60 * 1000;

async function loadAds(): Promise<AdItem[]> {
  const PROXY = "/api/ad-space";
  const hit = _cache.get(PROXY);
  if (hit && Date.now() - hit.at < _TTL) return hit.items;

  const res = await fetch(PROXY);
  if (!res.ok) throw new Error(`/api/ad-space returned ${res.status}`);

  const json: AdApiResponse = await res.json();
  const items = (json.data ?? []).flat();
  _cache.set(PROXY, { items, at: Date.now() });
  return items;
}

// ─────────────────────────────────────────────────────────────
// Fix lazyload → real src
// ─────────────────────────────────────────────────────────────

function fixAdCode(raw: string): string {
  return (
    raw
      // Replace lazyload with real src
      .replace(/src="data:image\/gif[^"]*"\s+data-src="([^"]+)"/g, 'src="$1"')
      .replace(/data-src="([^"]+)"/g, 'src="$1"')
      .replace(/\s*class="lazyload"/g, "")
      // Add target="_blank" and rel for all <a> tags
      .replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ')
  );
}

// ─────────────────────────────────────────────────────────────
// Individual marquee item
// ─────────────────────────────────────────────────────────────

function extractImageSrc(html: string): string | null {
  const match = html.match(/src="([^"]+)"/);
  if (match && match[1].includes("data:image")) {
    const dataSrcMatch = html.match(/data-src="([^"]+)"/);
    return dataSrcMatch ? dataSrcMatch[1] : null;
  }
  return match ? match[1] : null;
}

function MarqueeItem({
  ad,
  isMobile,
  itemHeight,
  index,
  gap,
  speed,
  paused,
}: {
  ad: AdItem;
  isMobile: boolean;
  itemHeight: number;
  index: number;
  gap: number;
  speed: number;
  paused: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let animationFrame: number;
    let pos = window.innerWidth + index * (itemHeight * 2 + gap);

    const step = () => {
      if (!paused) {
        pos -= speed;
        if (el.offsetWidth + pos < 0) {
          pos = window.innerWidth + gap;
        }
        el.style.transform = `translateX(${pos}px)`;
      }
      animationFrame = requestAnimationFrame(step);
    };

    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [index, itemHeight, gap, speed, paused]);

  const rawCode = isMobile
    ? ad.ad_code_mobile || ad.ad_code_desktop
    : ad.ad_code_desktop;

  if (!rawCode) return null;

  // Parse HTML string into React nodes safely
  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: 0,
        width: itemHeight * 2,
        height: itemHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      dangerouslySetInnerHTML={{ __html: fixAdCode(rawCode) }}
    />
  );
}
// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────

const BRAND = "#f79413";

export default function CollaborationMarquee({
  heading = "We Are Collaborated With ▼",
  speed = 1, // pixels per frame
  gap = 48,
  itemHeight = 80,
  lang = 1,
  className = "",
  only = "collaborated",
}: CollaborationMarqueeProps) {
  const [ads, setAds] = useState<AdItem[]>([]);
  const [isMobile, setMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);

  // Detect mobile
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Fetch ads
  useEffect(() => {
    loadAds()
      .then((items) => {
        const matched = items.filter(
          (i) =>
            i.ad_space === only &&
            i.lang_id === String(lang) &&
            (i.ad_code_desktop || i.ad_code_mobile),
        );
        setAds(matched);
      })
      .catch((err) => console.error("[CollaborationMarquee] load error:", err))
      .finally(() => setLoading(false));
  }, [lang]);

  if (!loading && ads.length === 0) return null;

  return (
    <>
      <style>{`
        .cm-root {
          background-color: ${BRAND};
          width: 100%;
          overflow: hidden;
          padding: 10px 0 10px;
          position: relative;
        }
        .cm-root::before,
        .cm-root::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          width: 80px;
          z-index: 2;
          pointer-events: none;
        }
        .cm-root::before { left: 0; background: linear-gradient(to right, ${BRAND}, transparent); }
        .cm-root::after { right: 0; background: linear-gradient(to left, ${BRAND}, transparent); }

        .cm-heading {
          text-align: center;
          font-family: "monorope", "Poppins", sans-serif;
          font-size: clamp(14px, 2vw, 14px);
          font-weight: 700;
          text-transform: uppercase;
          color: #f79413;
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          user-select: none;
          letter-spacing: .9vh;
        }
          

        .cm-track-outer { width: 100%; overflow: hidden; position: relative; height: ${itemHeight}px; }

        /* Skeleton shimmer */
        @keyframes _cmShimmer {
          0% { background-position: 200% 0 }
          100% { background-position: -200% 0 }
        }
        .cm-skeleton {
          height: ${itemHeight}px;
          border-radius: 8px;
          background: linear-gradient(90deg, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0.30) 50%, rgba(255,255,255,0.15) 75%);
          background-size: 200% 100%;
          animation: _cmShimmer 1.4s ease infinite;
        }

        @media (max-width: 576px) {
          .cm-root { padding: 10px 0 10px; }
          .cm-root::before, .cm-root::after { width: 40px; }
          .cm-heading { font-size: 13px; margin-bottom: 16px; letter-spacing: 0.12em; }
        }
      `}</style>

      <div className="mt-3">
        <p className="cm-heading">{heading}</p>
        <section
          className={`cm-root ${className}`.trim()}
          aria-label="Collaboration partners"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="cm-track-outer">
            {loading ? (
              <div style={{ display: "flex", gap: gap, padding: "0 24px" }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="cm-skeleton"
                    style={{ width: itemHeight * 2.5 }}
                  />
                ))}
              </div>
            ) : (
              <div className="cm-track">
                {ads.map((ad, idx) => (
                  <MarqueeItem
                    key={ad.id}
                    ad={ad}
                    isMobile={isMobile}
                    itemHeight={itemHeight}
                    index={idx}
                    gap={gap}
                    speed={speed} // keep speed always
                    paused={paused}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
